import { parseEvalNatChoix } from './evalnatfunction';
import { uploadElementsImagesToR2, uploadImageToR2 } from './uploadToR2';
import { jugerDeveloppement, texteDuLatex } from './developpement';
import { ERREURS } from './catalogueErreurs';

const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Convertit bonne_reponse (lettre ou array de lettres) en contenu réel.
 * BanqueQuestion stocke "D" ou ["A","C"] → on remplace par le contenu du choix correspondant.
 * Si bonne_reponse est déjà un contenu (pas une lettre) → retourné tel quel.
 *
 * @param {Object} contenu - Le contenu de la question { choix, bonne_reponse, multi? }
 * @returns {string} - Le contenu de la bonne réponse (string ou JSON array stringify)
 */
export const resolveBonneReponse = (contenu) => {
    const br = contenu?.bonne_reponse;
    const choix = contenu?.choix;
    // Pas de choix (texte, association) → retourner en string
    if (!choix) return typeof br === 'string' ? br : JSON.stringify(br) || '';
    if (!br) return '';

    // Simple : une lettre A-H → contenu du choix
    if (typeof br === 'string') {
        const idx = LETTRES.indexOf(br);
        if (idx >= 0 && idx < choix.length) {
            const c = choix[idx];
            return typeof c === 'string' ? c : JSON.stringify(c);
        }
        // Pas une lettre → déjà un contenu
        return br;
    }

    // Multi : array de lettres ["A","C"] → JSON array de contenus
    if (Array.isArray(br)) {
        const allLetters = br.every(l => typeof l === 'string' && l.length === 1 && LETTRES.includes(l));
        if (allLetters && choix.length > 0) {
            const contenus = br.map(l => {
                const i = LETTRES.indexOf(l);
                const c = i >= 0 && i < choix.length ? choix[i] : l;
                return typeof c === 'string' ? c : JSON.stringify(c);
            });
            return JSON.stringify(contenus.sort());
        }
        // Déjà des contenus → retourner tel quel
        return JSON.stringify(br);
    }

    return String(br);
};

// ─── Fonctions de comparaison de réponses (utilisées par Quiz.jsx et CorrectionEval.jsx) ───

/**
 * Normalise une chaîne pour comparaison (accents, casse, espaces, apostrophes).
 */
export const normalizeAnswer = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(',', '.')
        .replace(/['''\u2019\u0027]/g, "'")
        // Espaces de MILLIERS (normale, insécable, fine) entre deux chiffres :
        // l'écran affiche « 4 512 354 » (utils/milliers.js), l'élève peut le
        // taper ainsi, la valeur stockée est « 4512354 ». Vaut aussi pour la
        // saisie stricte et les trous, où seule cette normalisation compare.
        // Un mot ou un zéro inutile n'est pas concerné.
        .replace(/(\d)[\s\u00a0\u202f]+(?=\d)/g, '$1')
        .trim();
};

/**
 * Normalise une EXPRESSION littérale saisie (« 2x − 8 », « -8 + 2·x ») pour la
 * comparaison d'expression (contenu.comparaison = 'expression', 2026-09-13) :
 * tous les espaces retirés, signe moins et croix unifiés, ² → ^2, virgule →
 * point, minuscules. L'équivalence algébrique n'est PAS calculée : le gen
 * fournit la liste finie des écritures admises (contenu.reponses_acceptees),
 * chacune normalisée de la même façon.
 */
export const normaliserExpression = (str) => String(str ?? '')
    .toLowerCase()
    .replace(/[\s  ]+/g, '')
    .replace(/[−–]/g, '-')
    .replace(/[×·⋅]/g, '*')
    .replace(/²/g, '^2')
    .replace(/,/g, '.');

/** Le `contenu` d'une question (payload gen ou question persistée, objet ou JSON) ; null sinon. */
const lireContenu = (question) => {
    if (!question) return null;
    let c = question.contenu;
    if (typeof c === 'string') { try { c = JSON.parse(c); } catch { c = null; } }
    return c && typeof c === 'object' && !Array.isArray(c) ? c : null;
};

/**
 * Les comparaisons d'une saisie d'EXPRESSION, posées par le gen dans
 * contenu.comparaison : 'expression' (liste finie des écritures admises,
 * 2026-09-13) ou 'developpement' (le correcteur calcule, utils/developpement.js,
 * 2026-09-24).
 */
const COMPARAISONS_EXPRESSION = ['expression', 'developpement'];

/** La comparaison d'expression d'une question ('expression', 'developpement'), null sinon. */
export const comparaisonDe = (question) => {
    const c = lireContenu(question)?.comparaison;
    return COMPARAISONS_EXPRESSION.includes(c) ? c : null;
};

/**
 * La saisie se compare-t-elle comme une EXPRESSION (liste ou calcul) ? C'est
 * aussi le signal de l'écran : l'étoile tapée devient ×, l'aperçu KaTeX suit
 * la frappe. À calculer au point d'appel, comme toleranceNumerique, et à
 * embarquer dans la fiche de réponse.
 */
export const comparaisonExpression = (question) => comparaisonDe(question) !== null;

/** Les écritures admises en plus de bonne_reponse (contenu.reponses_acceptees), [] sinon. */
export const reponsesAcceptees = (question) => {
    const l = lireContenu(question)?.reponses_acceptees;
    return Array.isArray(l) ? l.map(String) : [];
};

/**
 * Vérifie si une réponse est au format complexe [phrase|valeur] (tableaux vrai/faux).
 */
export const isComplexAnswer = (answer) => {
    return answer && answer.includes('[') && answer.includes('|');
};

/**
 * Vérifie si une string est du JSON (commence par [ ou {), hors format complexe.
 */
export const isJsonAnswer = (str) => {
    if (!str || typeof str !== 'string') return false;
    const trimmed = str.trim();
    // Une phrase à trous qui COMMENCE par un trou (« {{4512}} + 12 = … »)
    // n'est pas du JSON : un objet JSON commence par « {" », jamais par « {{ ».
    // Sans cette garde, elle partait dans la comparaison JSON et ratait la
    // normalisation des trous (constaté 2026-09-06, espaces de milliers).
    if (trimmed.startsWith('{{')) return false;
    return (trimmed.startsWith('[') || trimmed.startsWith('{')) && !isComplexAnswer(str);
};

/**
 * Normalise un JSON pour comparaison order-independent.
 * - Relier : trie les paires par source
 * - Ordonner : pas de tri (l'ordre compte)
 * - Classer : trie les clés + items dans chaque catégorie
 */
export const normalizeJsonForCompare = (jsonString) => {
    try {
        const parsed = JSON.parse(jsonString);

        // Relier : tableau de paires [["source","cible"], ...]
        if (Array.isArray(parsed) && parsed.length > 0 && Array.isArray(parsed[0])) {
            const sorted = [...parsed].sort((a, b) => String(a[0]).localeCompare(String(b[0])));
            return JSON.stringify(sorted);
        }

        // Ordonner : tableau simple ["a","b","c"]
        if (Array.isArray(parsed) && parsed.length > 0 && !Array.isArray(parsed[0])) {
            return JSON.stringify(parsed);
        }

        // Classer / Placer points : objet { "Cat": ["item1","item2"], ... } ou { "X": "3,5", ... }
        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
            const sorted = {};
            for (const key of Object.keys(parsed).sort()) {
                sorted[key] = Array.isArray(parsed[key]) ? [...parsed[key]].sort() : parsed[key];
            }
            return JSON.stringify(sorted);
        }

        return jsonString;
    } catch {
        return jsonString;
    }
};

/**
 * Extrait les mots des {{trous}} d'une phrase.
 *
 * ⚠ Contrainte sur le CONTENU d'un trou : la réponse à l'intérieur de `{{...}}`
 * ne doit PAS contenir d'accolade littérale `{` ou `}`. Le regex `[^{}]+`
 * exclut ces deux caractères pour deux raisons :
 *
 *   1. Permettre l'imbrication d'un trou dans un groupe KaTeX via la convention
 *      triple-accolade `{{{X}}}` — utilisée pour mettre un trou dans `\dfrac{...}`
 *      (ex: `\dfrac{{{20}}}{{{19}}}`). Sans cette exclusion, le regex capturerait
 *      `{20` au lieu de `20` et casserait le scoring. Voir preprocessBraces dans
 *      `components/VueQuestion/texteTrouMath.js` pour le rendu côté élève.
 *
 *   2. Éviter qu'un `}` mal placé ouvre une capture qui déborde sur le trou suivant.
 *
 * Les trous légitimes sont toujours des mots, nombres ou expressions courtes sans
 * accolades littérales (ex: `{{conjuguer}}`, `{{42}}`, `{{Théorème de Pythagore}}`).
 */
export const extractTrouWords = (str) => {
    const words = [];
    const re = /\{\{([^{}]+)\}\}/g;
    let m;
    while ((m = re.exec(str)) !== null) words.push(normalizeAnswer(m[1]));
    return words;
};

/**
 * Parse une réponse complexe au format [phrase|valeur] (tableaux vrai/faux, association).
 */
export const parseComplexAnswer = (answer) => {
    if (!answer) return null;
    const result = [];
    let depth = 0;
    let currentStart = -1;

    for (let i = 0; i < answer.length; i++) {
        if (answer[i] === '[') {
            if (depth === 0) currentStart = i;
            depth++;
        } else if (answer[i] === ']') {
            depth--;
            if (depth === 0 && currentStart !== -1) {
                const block = answer.substring(currentStart + 1, i);
                const pipeIndex = block.lastIndexOf('|');
                if (pipeIndex !== -1) {
                    result.push({ phrase: block.substring(0, pipeIndex), reponse: block.substring(pipeIndex + 1) });
                }
                currentStart = -1;
            }
        }
    }
    return result.length > 0 ? result : null;
};

/**
 * Compare deux réponses complexes au format [phrase|valeur] (order-independent).
 */
export const compareComplexAnswers = (studentAnswer, correctAnswer) => {
    const studentParsed = parseComplexAnswer(studentAnswer);
    const correctParsed = parseComplexAnswer(correctAnswer);

    if (!studentParsed || !correctParsed) return false;
    if (studentParsed.length !== correctParsed.length) return false;

    const studentSet = new Set(
        studentParsed.map(item => `${normalizeAnswer(item.phrase)}|${normalizeAnswer(item.reponse)}`)
    );
    const correctSet = new Set(
        correctParsed.map(item => `${normalizeAnswer(item.phrase)}|${normalizeAnswer(item.reponse)}`)
    );

    if (studentSet.size !== correctSet.size) return false;
    for (const item of studentSet) {
        if (!correctSet.has(item)) return false;
    }
    return true;
};

/**
 * Compare deux réponses de façon intelligente selon leur format.
 * Gère : string simple, JSON (relier/ordonner/classer), texte à trous, complexe [phrase|valeur].
 *
 * @param {string} studentAnswer - Réponse de l'élève
 * @param {string} correctAnswer - Bonne réponse
 * @param {string} correctAnswerImage - URL image de la bonne réponse (optionnel)
 * @returns {boolean} true si correct
 */

// ─── Programme de construction : comparaison géométrique ───

/**
 * Détecte une bonne_reponse de programme de construction : un objet JSON dont
 * TOUTES les valeurs sont des shapes ({cx,cy,r} ou type line/segment/ray).
 * Aucun autre format de réponse n'a cette forme (relier = array d'arrays,
 * classer = objet d'arrays de strings, placer_points = objet de strings) —
 * le sniffing est donc sans faux positif, et il évite de faire transiter le
 * subType jusqu'à chaque appelant de compareAnswers (vieilles données incluses).
 */
export const isConstructionShapesJson = (str) => {
    if (typeof str !== 'string' || !str.trim().startsWith('{')) return false;
    try {
        const obj = JSON.parse(str);
        const vals = Object.values(obj);
        if (vals.length === 0) return false;
        return vals.every((v) => v && typeof v === 'object' && (
            (typeof v.cx === 'number' && typeof v.r === 'number')
            || v.type === 'line' || v.type === 'segment' || v.type === 'ray'
        ));
    } catch { return false; }
};

/**
 * Compare une shape élève à la shape attendue, avec tolérance géométrique (px).
 * - line/segment : extrémités permutables (l'élève clique dans l'ordre qu'il veut)
 * - ray : angle ±2° (la réponse élève n'a pas les champs descriptifs from/labelEnd)
 * - cercle (pas de champ type — compat historique) : centre + rayon à la tolérance
 */
export const shapeMatches = (got, exp, tolerance = 12) => {
    if (!got || !exp) return false;
    const estTrait = (s) => s.type === 'line' || s.type === 'segment';
    if (estTrait(exp) && estTrait(got)) {
        const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
        return (d(got.p1, exp.p1) < tolerance && d(got.p2, exp.p2) < tolerance)
            || (d(got.p1, exp.p2) < tolerance && d(got.p2, exp.p1) < tolerance);
    }
    if (exp.type === 'ray' && got.type === 'ray') {
        const delta = Math.abs(((got.angleDeg - exp.angleDeg) % 360 + 360) % 360);
        return Math.min(delta, 360 - delta) <= 2;
    }
    if (!exp.type && !got.type) {
        return Math.hypot(got.cx - exp.cx, got.cy - exp.cy) < tolerance
            && Math.abs(got.r - exp.r) < tolerance;
    }
    return false;
};

/**
 * Score d'un programme de construction : TOUTES les tâches attendues doivent
 * avoir une shape élève géométriquement correspondante. Les clés surnuméraires
 * de la réponse élève (`__extra` des segments auto-tracés) sont ignorées.
 */
export const compareProgrammeConstruction = (studentJson, expectedJson, tolerance = 12) => {
    let student, expected;
    try {
        student = JSON.parse(studentJson || '{}');
        expected = JSON.parse(expectedJson || '{}');
    } catch { return false; }
    const ids = Object.keys(expected);
    if (ids.length === 0) return false;
    return ids.every((id) => shapeMatches(student[id], expected[id], tolerance));
};

// ─── Placer les aiguilles (horloge) : comparaison angulaire ───

/**
 * Détecte une bonne_reponse « placer_aiguilles » : un objet JSON avec la clé
 * discriminante `aiguilles` = { h, min, sec|null }. Aucun autre format ne
 * porte cette clé — sniffing sans faux positif, même motif que
 * isConstructionShapesJson (le subType ne transite pas jusqu'au scoring).
 */
export const isAiguillesJson = (str) => {
    if (typeof str !== 'string' || !str.trim().startsWith('{')) return false;
    try {
        const obj = JSON.parse(str);
        return !!(obj && obj.aiguilles && typeof obj.aiguilles === 'object'
            && typeof obj.aiguilles.h === 'number' && typeof obj.aiguilles.min === 'number');
    } catch { return false; }
};

/** Écart angulaire minimal entre deux directions (degrés, [0 ; 180]). */
const ecartAngulaire = (a, b) => {
    const d = Math.abs(((a - b) % 360 + 360) % 360);
    return Math.min(d, 360 - d);
};

/**
 * Une aiguille posée à `angleDeg` (depuis 12 h, sens horaire) est-elle juste
 * pour la cible { h, min, sec } ? Règles pédagogiques (prof 2026-08-22) :
 * - minutes / secondes : le bon repère de 5 minutes (aimantées au relâcher,
 *   ±3° absorbe les flottants) ;
 * - heures, min = 0 : PILE sur le chiffre (±6°) — le seul cas où c'est juste ;
 * - heures, min ≠ 0 : STRICTEMENT à l'intérieur de l'intervalle ]h ; h+1[
 *   (marge de 2° à chaque borne : plus petite que les 2,5° qui séparent la
 *   position exacte de min = 5 ou 55 du chiffre voisin) — aucune exigence de
 *   proportionnalité, « pile sur le chiffre » est LA seule vraie erreur.
 */
export const aiguilleJuste = (nom, angleDeg, cible) => {
    if (typeof angleDeg !== 'number' || !Number.isFinite(angleDeg)) return false;
    if (nom === 'minutes') return ecartAngulaire(angleDeg, cible.min * 6) <= 3;
    if (nom === 'secondes') {
        return cible.sec != null && ecartAngulaire(angleDeg, cible.sec * 6) <= 3;
    }
    const h = ((cible.h % 12) + 12) % 12;
    if (cible.min === 0) return ecartAngulaire(angleDeg, h * 30) <= 6;
    const a = ((angleDeg % 360) + 360) % 360;
    return a > h * 30 + 2 && a < (h + 1) * 30 - 2;
};

/**
 * Score « placer_aiguilles » : réponse élève = angles posés
 * { heures, minutes, secondes? }, bonne_reponse = { aiguilles: {h, min, sec} }.
 * Toutes les aiguilles demandées doivent être justes.
 */
export const comparePlacerAiguilles = (studentJson, expectedJson) => {
    let student, expected;
    try {
        student = JSON.parse(studentJson || '{}');
        expected = JSON.parse(expectedJson || '{}');
    } catch { return false; }
    const cible = expected.aiguilles;
    if (!cible || !student || typeof student !== 'object') return false;
    if (!aiguilleJuste('heures', student.heures, cible)) return false;
    if (!aiguilleJuste('minutes', student.minutes, cible)) return false;
    if (cible.sec != null && !aiguilleJuste('secondes', student.secondes, cible)) return false;
    return true;
};

/**
 * Égalité de VALEUR entre deux écritures déjà normalisées (virgule → point) :
 * 2.3 = 2.30 = 2.300 — le même nombre compte juste, quelle que soit l'écriture.
 * Non numérique (« oui », « 3/4 », « 14h05 ») → NaN → false : l'appelant garde
 * l'égalité de chaînes. Chaîne vide jamais égale (Number('') vaut 0, piège).
 */
const memeValeurNumerique = (a, b) => {
    if (a === '' || b === '') return false;
    // Espaces de MILLIERS strippés (revue 644, 2026-07-31) : les clés affichées
    // « 4 200 » doivent égaler la frappe naturelle « 4200 » (espace, insécable,
    // fine). Sans danger pour les mots : « pot de fleurs » devient NaN et
    // retombe sur la comparaison de chaînes de l'appelant.
    const sa = String(a).replace(/[\s  ]/g, '');
    const sb = String(b).replace(/[\s  ]/g, '');
    if (sa === '' || sb === '') return false;
    const na = Number(sa);
    const nb = Number(sb);
    return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
};

/**
 * Un trou est juste si le mot de l'élève égale le mot attendu (les deux déjà
 * normalisés par extractTrouWords), ou, en question tolérante, s'ils ont la
 * même valeur numérique (2,30 = 2,3). C'est LA règle du scoring, une seule :
 * compareAnswers l'applique trou par trou, et la correction sur place
 * (interactifs/corrige.jsx, lot 4 du 2026-09-21) la réutilise pour marquer
 * chaque trou exactement comme Quiz l'a jugé.
 */
export const trouJuste = (mot, attendu, options = {}) => (
    mot === attendu || (options.tolerant === true && memeValeurNumerique(mot, attendu))
);

/** Le flag `contenu.calculatrice` de la question (contenu peut arriver en string JSON côté DRF), ou undefined. */
const flagCalculatrice = (question) => {
    if (!question) return undefined;
    let c = question.contenu;
    if (typeof c === 'string') { try { c = JSON.parse(c); } catch { c = null; } }
    return c && typeof c === 'object' && !Array.isArray(c) ? c.calculatrice : undefined;
};

/**
 * La question autorise-t-elle la calculatrice ? Flag PÉDAGOGIQUE posé par les
 * générateurs dans `contenu` (canal persisté, comme `strict`/`sousType`) :
 * les 6e mesures (639-646) le posent à true, les fractions explicitement à
 * false. Strictement `=== true`, ou `'cos'` (la calculatrice avec les
 * touches cos et arccos, 4e 1090, 2026-09-24) : champ absent (anciennes
 * questions, EvalNat) = pas de calculatrice. Seuls points de lecture du flag
 * (avec calculatriceCosinus) — jamais d'accès direct
 * `question.contenu.calculatrice` dans les composants.
 */
export const calculatriceAutorisee = (question) => {
    const f = flagCalculatrice(question);
    return f === true || f === 'cos';
};

/** La calculatrice montre-t-elle les touches cos et arccos ? (`contenu.calculatrice === 'cos'`) */
export const calculatriceCosinus = (question) => flagCalculatrice(question) === 'cos';

/**
 * La question a-t-elle droit à la tolérance numérique (2,3 = 2,30) ?
 * OUI uniquement pour : saisie libre ('texte') et texte à trous en mode
 * SAISIE, sans flag `strict`. Tout le reste (QCM, drag, relier, classer…)
 * reste en comparaison exacte : l'élève y CHOISIT une écriture parmi des
 * propositions — tolérer « 2,30 » quand la bonne option est « 2,3 »
 * validerait un distracteur d'écriture. `strict: true` (posé par le gen
 * quand l'ÉCRITURE est la compétence testée, ex. zéros inutiles) désactive
 * la tolérance même en saisie.
 * Accepte un payload générateur ({ contenu }) comme une question persistée
 * ({ type_question, reponses } avec la ligne trou_meta).
 */
export const toleranceNumerique = (question) => {
    if (!question) return false;
    let c = question.contenu;
    if (typeof c === 'string') { try { c = JSON.parse(c); } catch { c = null; } }
    if (c && typeof c === 'object' && !Array.isArray(c)) {
        if (c.subType === 'texte_trou') return c.mode === 'saisie' && !c.strict;
        if (c.subType) return false;                       // autres interactifs → exact
        if (c.choix) return false;                          // QCM → exact
        if (c.bonne_reponse != null) return !c.strict;      // saisie libre
        return false;
    }
    const reps = question.reponses;
    if (Array.isArray(reps) && reps.length) {
        const meta = reps.find((r) => r && r.type === 'trou_meta');
        if (meta) return meta.mode === 'saisie' && !meta.strict;
        if ((question.type_question || question.type) === 'texte') return !(reps[0] && reps[0].strict);
        return false;
    }
    return (question.type_question || question.type) === 'texte';
};

/**
 * Les options de jugement d'une QUESTION (payload gen ou question persistée) :
 * tolérance numérique, comparaison d'expression et écritures admises. À
 * calculer UNE FOIS au point d'appel et à embarquer dans la fiche de réponse
 * (Quiz.jsx), que la correction relit ensuite via optionsDeFiche.
 */
/**
 * Des GROUPES DE TROUS SANS ORDRE, déclarés par le générateur dans
 * `contenu.trousInterchangeables` : `[[0, 1]]` dit que les trous 0 et 1 peuvent être
 * remplis dans n'importe quel ordre. Indices à partir de 0, dans l'ordre d'apparition
 * des `{{...}}` dans la phrase.
 *
 * POURQUOI un réglage déclaré, et non une détection : la correction ne voit que deux
 * chaînes de texte, jamais les mathématiques. « {{3}} + {{4}} » et « 9 × {{10}} + {{8}} »
 * ont la même forme, mais le second oppose un quotient à un reste et l'échange y est
 * FAUX. Seul l'auteur de la carte sait lequel est lequel (arbitrage prof 2026-09-24,
 * gens 907, 920 et 924 explicitement laissés stricts).
 *
 * DÉFAUT : aucun groupe, donc comparaison positionnelle — le comportement historique.
 * On ne déclare que là où l'ordre vient de la mise en page et non du sens.
 */
export const trousInterchangeables = (question) => {
    if (!question) return [];
    let c = question.contenu;
    if (typeof c === 'string') { try { c = JSON.parse(c); } catch { c = null; } }
    if (c && typeof c === 'object' && !Array.isArray(c) && Array.isArray(c.trousInterchangeables)) {
        return c.trousInterchangeables;
    }
    const reps = question.reponses;
    if (Array.isArray(reps) && reps.length) {
        const meta = reps.find((r) => r && r.type === 'trou_meta');
        if (meta && Array.isArray(meta.interchangeables)) return meta.interchangeables;
    }
    return [];
};

/**
 * Nettoie les groupes venus d'une donnée persistée : on ne fait confiance à rien.
 * Un groupe n'est retenu que s'il contient au moins deux indices ENTIERS distincts,
 * tous dans la phrase, et qu'aucun indice n'appartient déjà à un autre groupe.
 * Un groupe douteux est ignoré : la comparaison redevient stricte, jamais plus laxiste.
 */
const groupesValides = (groupes, nbTrous) => {
    if (!Array.isArray(groupes)) return [];
    const vus = new Set();
    const ok = [];
    groupes.forEach((g) => {
        if (!Array.isArray(g)) return;
        const indices = [...new Set(g)].filter(
            (i) => Number.isInteger(i) && i >= 0 && i < nbTrous && !vus.has(i),
        );
        if (indices.length < 2) return;
        indices.forEach((i) => vus.add(i));
        ok.push(indices);
    });
    return ok;
};

/**
 * Le verdict de CHAQUE trou, groupes sans ordre compris (2026-09-29) : pour le
 * trou i, `juste`, et `attendu`, l'indice du mot attendu qui lui revient. Hors
 * groupe, c'est le sien (i), jugé par trouJuste. Dans un groupe, chaque valeur
 * attendue ne sert qu'une fois : le trou prend la première valeur égale encore
 * libre ; un trou qui n'en trouve pas est faux et reçoit une valeur restée
 * libre, celle qu'il fallait écrire (jamais « attendu 4 » à côté d'un 4).
 * compareAnswers juge juste quand tous les trous le sont ; la correction sur
 * place, la relecture et la vue prof marquent trou par trou avec cette même
 * fonction, donc exactement comme le score. Les mots arrivent normalisés
 * (extractTrouWords, normalizeAnswer).
 */
export const jugerTrous = (motsEleve, motsAttendus, options = {}) => {
    const verdicts = motsAttendus.map((attendu, i) => ({
        juste: trouJuste(motsEleve[i] ?? '', attendu, options),
        attendu: i,
    }));
    groupesValides(options.interchangeables, motsAttendus.length).forEach((g) => {
        const libres = [...g];
        const fautifs = [];
        g.forEach((i) => {
            const j = libres.findIndex((k) => trouJuste(motsEleve[i] ?? '', motsAttendus[k], options));
            if (j === -1) { fautifs.push(i); return; }
            verdicts[i] = { juste: true, attendu: libres[j] };
            libres.splice(j, 1);
        });
        fautifs.forEach((i, r) => { verdicts[i] = { juste: false, attendu: libres[r] }; });
    });
    return verdicts;
};

export const optionsDeQuestion = (question) => ({
    tolerant: toleranceNumerique(question),
    expression: comparaisonExpression(question),
    acceptees: reponsesAcceptees(question),
    comparaison: comparaisonDe(question),
    interchangeables: trousInterchangeables(question),
});

/** Les mêmes options relues depuis une fiche de réponse déjà constituée ({ réponse, bonne_reponse, tolerant, expression, acceptees, comparaison, interchangeables }). */
export const optionsDeFiche = (fiche) => ({
    tolerant: fiche?.tolerant === true,
    expression: fiche?.expression === true,
    acceptees: Array.isArray(fiche?.acceptees) ? fiche.acceptees : [],
    comparaison: COMPARAISONS_EXPRESSION.includes(fiche?.comparaison) ? fiche.comparaison : null,
    interchangeables: Array.isArray(fiche?.interchangeables) ? fiche.interchangeables : [],
});

/**
 * `options.tolerant` (défaut false = comportement historique exact) : active
 * l'équivalence numérique par valeur — à calculer via toleranceNumerique(q)
 * au point d'appel, jamais en dur. `options.expression` + `options.acceptees`
 * (comparaison d'expression) : cf. optionsDeQuestion / optionsDeFiche.
 */
export const compareAnswers = (studentAnswer, correctAnswer, correctAnswerImage, options = {}) => {
    if (!studentAnswer) return false;

    // Comparaison directe
    if (studentAnswer === correctAnswer) return true;
    if (correctAnswerImage && studentAnswer === correctAnswerImage) return true;

    // Programme de construction — comparaison GÉOMÉTRIQUE dédiée, AVANT la
    // branche JSON : l'égalité structurelle est impossible ici (clés __extra
    // côté élève, extrémités p1/p2 permutables, ray sans from/labelEnd, ±2°).
    if (isConstructionShapesJson(correctAnswer)) {
        return compareProgrammeConstruction(studentAnswer, correctAnswer);
    }

    // Placer les aiguilles — comparaison ANGULAIRE dédiée, avant la branche
    // JSON (la réponse élève est en degrés posés, jamais égale structurellement)
    if (isAiguillesJson(correctAnswer)) {
        return comparePlacerAiguilles(studentAnswer, correctAnswer);
    }

    // JSON (relier, ordonner, classer)
    if (isJsonAnswer(studentAnswer) && isJsonAnswer(correctAnswer)) {
        return normalizeJsonForCompare(studentAnswer) === normalizeJsonForCompare(correctAnswer);
    }

    // Texte à trous — comparer les mots dans les {{}}, trou par trou.
    // En tolérant, chaque trou juge PAR VALEUR quand les deux côtés sont des
    // nombres (2,30 = 2,3) ; les trous textuels (« oui ») restent stricts.
    if (studentAnswer.includes('{{') && correctAnswer.includes('{{')) {
        const studentWords = extractTrouWords(studentAnswer);
        const correctWords = extractTrouWords(correctAnswer);
        if (studentWords.length !== correctWords.length) return false;
        // Trou par trou (jugerTrous) : sans groupe déclaré, le cas de l'immense
        // majorité des cartes, dans l'ordre, par la règle unique du trou
        // (trouJuste). Dans un groupe sans ordre, chaque réponse de l'élève prend
        // une valeur attendue ENCORE LIBRE : « 3 + 4 » et « 4 + 3 » passent,
        // « 3 + 3 » ne passe pas, la valeur 3 n'étant disponible qu'une fois.
        return jugerTrous(studentWords, correctWords, options).every((t) => t.juste);
    }

    // Expression littérale (options.expression, posé depuis contenu.comparaison
    // = 'expression' via comparaisonExpression(q)) : la saisie est juste si,
    // espaces et signes normalisés, elle égale bonne_reponse OU l'une des
    // écritures admises (options.acceptees, depuis contenu.reponses_acceptees).
    // Pas d'équivalence algébrique calculée : la liste est finie, produite par
    // le gen (« 2x - 8 », « -8 + 2x », « +2x - 8 »…). Sauf en DÉVELOPPEMENT
    // (options.comparaison = 'developpement') : pas de liste, le correcteur
    // calcule (égal au résultat, plus de parenthèse avec une somme).
    if (options.expression === true) {
        const eleve = normaliserExpression(studentAnswer);
        if (eleve === '') return false;
        if (options.comparaison === 'developpement') {
            return jugerDeveloppement(eleve, normaliserExpression(texteDuLatex(correctAnswer)));
        }
        const admises = [correctAnswer, ...(options.acceptees || [])].map(normaliserExpression);
        return admises.includes(eleve);
    }

    // Comparaison normalisée (accents, casse) — puis par valeur si tolérant
    const sa = normalizeAnswer(studentAnswer);
    const ca = normalizeAnswer(correctAnswer);
    if (sa === ca) return true;
    if (options.tolerant === true && memeValeurNumerique(sa, ca)) return true;

    return false;
};

// ─── Codes d'erreur (brief docs/BRIEF_LOT1_CODES_ERREUR.md, 2026-10-02) ───

/** Les mots des trous d'une phrase, à leur place, un trou vide (`{{}}`) compris (extractTrouWords le saute). */
const motsDesTrousEnPlace = (str) => {
    const mots = [];
    const re = /\{\{([^{}]*)\}\}/g;
    let m;
    while ((m = re.exec(str)) !== null) mots.push(normalizeAnswer(m[1]));
    return mots;
};

/**
 * Les erreurs reconnues dans une réponse FAUSSE : la réponse de l'élève est
 * rapprochée des erreurs que l'exerciseur a rangées dans `contenu.erreurs`,
 * avec le même juge que la correction (une erreur se reconnaît comme une bonne
 * réponse : « 130,0 » retrouve « 130 »). `[]` pour une réponse juste, blanche,
 * ou dont l'erreur n'est pas connue ; chaque code une seule fois.
 *
 * Chaque erreur part avec LE MORCEAU de réponse qui l'a déclenchée (03/10/2026 :
 * le professeur voyait la réponse entière sous un nom qu'une seule ligne
 * portait) : `{ code, donnee, bonne }`, où `donnee` et `bonne` sont ce morceau
 * côté élève et côté corrigé, écrits comme la réponse entière l'est : la ligne
 * d'une série à trous, une ligne `[phrase|valeur]` d'un vrai/faux, le texte du
 * choix coché, une paire `[[source, cible]]`, un point `{ A: position }`.
 *
 * SEULS deux formats sont nommés en amont (décision du prof, 03/10/2026) : le
 * QCM, à un ou plusieurs choix ([{ code, valeur }] : le texte du choix), et le
 * tableau vrai/faux ([{ code, ligne }] : l'autre colonne cochée sur cette ligne),
 * dont chaque nom a passé la lecture à l'aveugle. Un texte à trous (étiquettes
 * comprises, tant que leurs noms n'ont pas été lus), une saisie, un placer, un
 * relier ne sont JAMAIS nommés ici : ce que l'élève a produit librement ne dit
 * pas d'où il vient (un lecteur, avec l'énoncé, le dira plus tard) ; les tables
 * que des gens rangent encore pour ces formats restent sans effet (le registre
 * les retire d'ailleurs, src/generators/erreursNommables.js). Seuls partent les
 * codes du catalogue (catalogueErreurs.js) : un code inconnu (question ancienne)
 * n'est jamais envoyé, la réponse part alors sans code.
 *
 * @param {Object} question - la question jouée (son `contenu`)
 * @param {Object} fiche - la fiche de réponse ({ réponse, bonne_reponse, score, options de correction })
 * @returns {{ code: string, donnee: string, bonne: string }[]}
 */
export const trouverErreurs = (question, fiche) => {
    const reponse = fiche?.réponse;
    if (!reponse || typeof reponse !== 'string' || fiche.score === 1) return [];
    const contenu = lireContenu(question) || {};
    const erreurs = Array.isArray(contenu.erreurs) ? contenu.erreurs.filter((e) => e && e.code) : [];
    const options = optionsDeFiche(fiche);
    const trouvees = [];
    // Un code une seule fois, avec le premier morceau de réponse qui l'a déclenché.
    const noter = (code, donnee, attendu) => {
        if (!ERREURS[code] || trouvees.some((t) => t.code === code)) return;
        trouvees.push({ code, donnee: String(donnee ?? ''), bonne: String(attendu ?? '') });
    };
    const bonne = typeof fiche.bonne_reponse === 'string' ? fiche.bonne_reponse : '';
    const json = (texte) => { try { return JSON.parse(texte); } catch { return undefined; } };

    // Tableau vrai/faux : chaque ligne cochée dans la mauvaise colonne.
    if (isComplexAnswer(reponse) && isComplexAnswer(bonne)) {
        const attendues = new Map((parseComplexAnswer(bonne) || []).map((l) => [normalizeAnswer(l.phrase), l]));
        (parseComplexAnswer(reponse) || []).forEach((l) => {
            const phrase = normalizeAnswer(l.phrase);
            const attendue = attendues.get(phrase);
            if (!attendue || normalizeAnswer(attendue.reponse) === normalizeAnswer(l.reponse)) return;
            erreurs
                .filter((e) => e.ligne !== undefined && normalizeAnswer(String(e.ligne)) === phrase)
                .forEach((e) => noter(e.code, `[${l.phrase}|${l.reponse}]`, `[${attendue.phrase}|${attendue.reponse}]`));
        });
        return trouvees;
    }

    // Appariement et placer des points : pas de nom en amont (l'élève produit librement).
    if (contenu.subType === 'relier' || contenu.subType === 'placer_points' || contenu.subType === 'placer_points_2d') return [];

    // QCM à plusieurs réponses : chaque choix coché qui est une erreur connue.
    if (contenu.multi && reponse.trim().startsWith('[')) {
        const coches = json(reponse);
        if (!Array.isArray(coches)) return [];
        coches.forEach((choix) => erreurs
            .filter((e) => e.valeur !== undefined && e.trou === undefined && e.ligne === undefined && e.point === undefined
                && compareAnswers(String(choix), String(e.valeur), null, options))
            .forEach((e) => noter(e.code, choix, bonne)));
        return trouvees;
    }

    // Texte à trous : pas de nom en amont (décision du 03/10/2026, étiquettes
    // comprises). Le code qui suit reste prêt pour le jour où les noms des
    // étiquettes auront passé la lecture à l'aveugle : chaque trou faux, parmi
    // les erreurs de ce trou (ou de tout son groupe sans ordre), un trou vide
    // sans code, le morceau = la ligne qui porte le trou.
    if (reponse.includes('{{') && bonne.includes('{{')) {
        if (contenu.mode !== 'drag' || !contenu.etiquettesNommees) return [];
        const motsEleve = motsDesTrousEnPlace(reponse);
        const motsAttendus = motsDesTrousEnPlace(bonne);
        if (motsEleve.length !== motsAttendus.length) return [];
        const lignesEleve = reponse.split('\n');
        const lignesAttendues = bonne.split('\n');
        const ligneDuTrou = (lignes) => lignes.flatMap((l, k) => (l.match(/\{\{[^{}]*\}\}/g) || []).map(() => k));
        const chezEleve = ligneDuTrou(lignesEleve);
        const chezCorrige = ligneDuTrou(lignesAttendues);
        const morceau = (i) => (lignesEleve.length === lignesAttendues.length && chezEleve[i] !== undefined && chezCorrige[i] === chezEleve[i]
            ? [lignesEleve[chezEleve[i]], lignesAttendues[chezEleve[i]]]
            : [reponse, bonne]);
        const groupes = groupesValides(options.interchangeables, motsAttendus.length);
        jugerTrous(motsEleve, motsAttendus, options).forEach((verdict, i) => {
            if (verdict.juste || motsEleve[i] === '') return;
            const places = groupes.find((g) => g.includes(i)) || [i];
            erreurs
                .filter((e) => places.includes(e.trou) && e.valeur !== undefined
                    && trouJuste(motsEleve[i], normalizeAnswer(String(e.valeur)), options))
                .forEach((e) => noter(e.code, ...morceau(i)));
        });
        return trouvees;
    }

    // QCM : le choix coché égale une valeur fausse connue. Une saisie libre n'est pas nommée en amont.
    if (!Array.isArray(contenu.choix) || !contenu.choix.length) return [];
    erreurs
        .filter((e) => e.trou === undefined && e.ligne === undefined && e.point === undefined && e.valeur !== undefined
            && compareAnswers(reponse, String(e.valeur), null, options))
        .forEach((e) => noter(e.code, reponse, bonne));
    return trouvees;
};

/**
 * L'énoncé d'une question, pour le professeur (défis, brief lot 1) : la phrase
 * à trous quand il y en a une (le texte d'une Série flash n'est que la
 * consigne), sinon le texte. Rien quand il porte une figure (svg, img), que
 * l'on ne stocke pas : le nom de la carte dit alors de quel exercice il s'agit.
 */
export const enonceDeQuestion = (question) => {
    const texte = lireContenu(question)?.phrase || question?.texte || '';
    if (typeof texte !== 'string' || !texte || /<(svg|img)\b/i.test(texte)) return undefined;
    return texte;
};

/**
 * La copie d'un quiz en ligne, annotée avant l'envoi : `aide: true` là où le
 * coup de pouce a été pris (`aides` : { 'catégorie::question': true }), et
 * `erreurs` sur chaque réponse donnée (trouverErreurs : chaque code avec le
 * morceau de réponse qui l'a déclenché). Une fiche blanche part telle quelle.
 * Clone : l'objet reçu n'est jamais modifié.
 */
export const annoterReponses = (reponsesObj, questions, aides = {}) => {
    const out = {};
    Object.keys(reponsesObj || {}).forEach((cat) => {
        out[cat] = {};
        Object.keys(reponsesObj[cat] || {}).forEach((qKey) => {
            const fiche = reponsesObj[cat][qKey];
            out[cat][qKey] = {
                ...fiche,
                ...(aides[`${cat}::${qKey}`] ? { aide: true } : {}),
                ...(fiche && fiche.réponse ? { erreurs: trouverErreurs(questions?.[cat]?.[qKey], fiche) } : {}),
            };
        });
    });
    return out;
};

/**
 * Détecte le type de question depuis un objet index (question map).
 * Retourne : 'qcm', 'complex', 'texte', 'relier', 'ordonner', 'texte_trou', 'classer'
 *
 * @param {Object} index - L'objet question depuis la map (avec type_question, reponses, bonne_reponse)
 * @returns {string} Le type détecté
 */
export const detectQuestionType = (index) => {
    if (!index) return 'qcm';

    // 1. Type explicite depuis le modèle
    if (index.type_question === 'qcm_multiple') return 'qcm';  // QCM multi affiché comme QCM, le multi est détecté via correctAnswerIndexes
    if (index.type_question === 'texte') return 'texte';
    if (index.type_question === 'association') {
        // Détecter le sous-type depuis reponses[0].type — 'paire_leurre' (cible
        // surnuméraire d'un relier) est rattaché à 'relier' : une ligne leurre en
        // tête de liste ne doit pas casser la détection
        const firstType = index.reponses?.[0]?.type || '';
        if (firstType === 'paire' || firstType === 'paire_leurre') return 'relier';
        if (firstType === 'ordre') return 'ordonner';
        if (firstType === 'trou' || firstType === 'trou_meta') return 'texte_trou';
        if (firstType === 'categorie') return 'classer';
        return 'relier'; // fallback association
    }

    // 2. Fallback — détection depuis la structure des données
    const br = index.bonne_reponse || '';
    if (isComplexAnswer(br)) return 'complex';
    if (br.includes('{{')) return 'texte_trou';
    if (isJsonAnswer(br)) {
        try {
            const parsed = JSON.parse(br);
            if (Array.isArray(parsed) && parsed.length > 0 && Array.isArray(parsed[0])) return 'relier';
            if (Array.isArray(parsed)) return 'ordonner';
            if (typeof parsed === 'object') return 'classer';
        } catch { /* ignore */ }
    }

    return 'qcm';
};

/**
 * Transforme une question API (reponses + bonne_reponse + type_question)
 * en objet preview unifié pour QuestionPreviewModal.
 *
 * Retourne un objet { texte, image, type_question, elements_additionnels, contenu }
 * où `contenu` est le format unifié selon le type :
 *   - QCM :          { choix: [...], bonne_reponse: 'A' }
 *   - Texte libre :  { bonne_reponse: '42', strict: false }
 *   - Relier :       { subType: 'relier', pairs: [{source, target}, ...] }
 *   - Ordonner :     { subType: 'ordonner', items: [...] }
 *   - Texte à trous : { subType: 'texte_trou', phrase: '...', mode: 'drag', distracteurs: [...] }
 *   - Classer :      { subType: 'classer', categories: [{name, items}, ...] }
 *
 * @param {Object} question - La question depuis l'API
 * @returns {Object} Question formatée pour QuestionPreviewModal
 */
export const buildPreviewContenu = (question) => {
    if (!question) return null;

    // Si la question a déjà un champ `contenu` (BanqueQuestion) → retourner directement
    if (question.contenu && typeof question.contenu === 'object' && !Array.isArray(question.contenu)) {
        return {
            texte: question.texte,
            image: question.image,
            type_question: question.type_question || question.type || 'qcm',
            elements_additionnels: question.elements_additionnels,
            contenu: question.contenu,
        };
    }

    const typeQuestion = question.type_question || 'qcm';
    const reponses = question.reponses || [];

    // Base commune
    const preview = {
        texte: question.texte,
        image: question.image,
        type_question: typeQuestion,
        elements_additionnels: question.elements_additionnels,
    };

    // ─── Tableau (stocké comme QCM mais format [ligne|colonne]) — doit être traité avant QCM ───
    // Exclure qcm_multiple pour éviter la confusion avec un JSON array dans bonne_reponse
    if (typeQuestion !== 'qcm_multiple' && question.bonne_reponse?.includes('[') && question.bonne_reponse?.includes('|')) {
        preview.contenu = {
            choix: [],
            bonne_reponse: question.bonne_reponse,
        };
        return preview;
    }

    // ─── QCM Multiple : bonne_reponse est un JSON array des contenus ───
    if (typeQuestion === 'qcm_multiple') {
        const choix = parseEvalNatChoix(reponses);
        let bonnesLettres = [];
        try {
            const bonnesContenus = JSON.parse(question.bonne_reponse);
            if (Array.isArray(bonnesContenus)) {
                bonnesLettres = bonnesContenus.map(val => {
                    const idx = reponses.findIndex(r => r.contenu === val || (r.type === 'image' && r.image === val));
                    return idx >= 0 ? LETTRES[idx] : null;
                }).filter(Boolean);
            }
        } catch { /* bonne_reponse n'est pas du JSON — fallback vide */ }
        preview.contenu = {
            choix,
            bonne_reponse: bonnesLettres,
            multi: true,
        };
        return preview;
    }

    // ─── QCM simple : transformer reponses en choix A/B/C/D ───
    if (typeQuestion === 'qcm') {
        const bonneIndex = reponses.findIndex(
            r => r.contenu === question.bonne_reponse || (r.type === 'image' && r.image === question.bonne_reponse)
        );
        preview.contenu = {
            choix: parseEvalNatChoix(reponses),
            bonne_reponse: bonneIndex >= 0 ? LETTRES[bonneIndex] : '',
        };
        return preview;
    }

    // ─── Texte libre : reponses[0] contient les métadonnées (strict) ───
    if (typeQuestion === 'texte') {
        const meta = reponses[0] || {};
        preview.contenu = {
            bonne_reponse: question.bonne_reponse || '',
            strict: meta.strict || false,
            suffixe: meta.suffixe || '',
        };
        return preview;
    }

    // ─── Association : reconstruire le contenu selon le sous-type ───
    if (typeQuestion === 'association') {
        // Détecter le sous-type depuis les réponses
        const firstType = reponses[0]?.type || '';

        if (firstType === 'paire' || firstType === 'paire_leurre') {
            // Relier : reponses = [{ type: 'paire', source, target }, ...]
            //          + éventuelles lignes { type: 'paire_leurre', target, code }
            //          (cibles surnuméraires) → réintégrées en cibles_leurres
            const leurres = reponses
                .filter(r => r.type === 'paire_leurre')
                .map(r => ({ target: r.target, code: r.code || '' }));
            preview.contenu = {
                subType: 'relier',
                pairs: reponses.filter(r => r.type === 'paire').map(r => ({ source: r.source, target: r.target })),
                ...(leurres.length ? { cibles_leurres: leurres } : {}),
            };
        } else if (firstType === 'ordre') {
            // Ordonner : reponses = [{ type: 'ordre', contenu, position }, ...]
            const sorted = [...reponses].sort((a, b) => a.position - b.position);
            preview.contenu = {
                subType: 'ordonner',
                items: sorted.map(r => r.contenu),
            };
        } else if (firstType === 'trou' || firstType === 'trou_meta') {
            // Texte à trous : reponses = [{ type: 'trou', contenu, isAnswer }, ..., { type: 'trou_meta', phrase, mode, strict }]
            const meta = reponses.find(r => r.type === 'trou_meta') || {};
            const distracteurs = reponses
                .filter(r => r.type === 'trou' && !r.isAnswer)
                .map(r => r.contenu);
            preview.contenu = {
                subType: 'texte_trou',
                phrase: meta.phrase || question.bonne_reponse || '',
                mode: meta.mode || 'drag',
                distracteurs,
            };
        } else if (firstType === 'categorie') {
            // Classer : reponses = [{ type: 'categorie', contenu: 'Pair', items: ['4','6'] }, ...]
            preview.contenu = {
                subType: 'classer',
                categories: reponses.map(r => ({ name: r.contenu, items: r.items || [] })),
            };
        } else {
            preview.contenu = {};
        }

        return preview;
    }

    // Fallback
    preview.contenu = {};
    return preview;
};

/**
 * Extrait la config de tableau depuis elements_additionnels ou bonne_reponse.
 * Retourne un objet { type, colonnes, lignes, bonnesReponses } ou null.
 */
export const getTableElementConfig = (question) => {
    // 1. Depuis elements_additionnels
    if (question?.elements_additionnels) {
        try {
            const data = typeof question.elements_additionnels === 'string'
                ? JSON.parse(question.elements_additionnels)
                : question.elements_additionnels;
            const tableEl = data?.elements?.find(el => el.type === 'table');
            if (tableEl?.config) return tableEl.config;
        } catch { /* ignore */ }
    }

    // 2. Depuis bonne_reponse au format [phrase|valeur]
    if (question?.bonne_reponse?.includes('[') && question?.bonne_reponse?.includes('|')) {
        const parsed = parseComplexAnswer(question.bonne_reponse);
        if (!parsed) return null;

        const lignes = parsed.map(item => item.phrase);
        const uniqueReponses = [...new Set(parsed.map(item => item.reponse))];
        const isVraiFaux = uniqueReponses.every(r => ['vrai', 'faux'].includes(r.toLowerCase()));
        const colonnes = isVraiFaux ? ['Vrai', 'Faux'] : uniqueReponses;
        const bonnesReponses = parsed.map(item =>
            isVraiFaux ? (item.reponse.toLowerCase() === 'vrai' ? 0 : 1) : colonnes.indexOf(item.reponse)
        );

        const allReponses = parsed.map(item => item.reponse);
        const hasDuplicates = allReponses.length !== new Set(allReponses).size;
        let tableType = 'association';
        if (isVraiFaux) tableType = 'vraiFaux';
        else if (hasDuplicates) tableType = 'colonnesLibres';

        return { type: tableType, titre: '', sousTitre: '', colonnes, lignes, bonnesReponses };
    }

    return null;
};

// ─── Build payload pour POST /banque-questions/ ───

/**
 * Prépare 1 question pour l'API : upload ses images base64 vers R2, retourne le payload nettoyé.
 * Le contenu (QCM, texte, association, tableau) doit déjà être structuré en amont.
 *
 * Utilisé par :
 * - QuestionForm.handleSubmit (skipUpload=false → upload, skipUpload=true → draftMode)
 * - ImportQuestionsHTML.handleCreate (upload R2 pour chaque question avant bulk POST)
 *
 * @param {Object} question - { texte, type, contenu, elements_additionnels, attendu?, competence?, niveau?, matiere? }
 * @param {Object} options
 * @param {boolean} options.skipUpload - true = pas d'upload R2, garde les base64 (draftMode)
 * @param {string}  options.source - préfixe "Tiré de..." dans l'énoncé (import HTML)
 * @returns {Promise<Object>} Payload prêt (sans etablissement — ajouté par le parent)
 */
export const buildQuestionPayload = async (question, options = {}) => {
    const { skipUpload = false, source } = options;
    const { texte, type, contenu, elements_additionnels, attendu = null, competence = null, niveau, matiere } = question;

    // 1. Éléments additionnels (images, expressions, tables)
    //    uploadElementsImagesToR2 est safe : ne touche que type=image+imageData, retourne le reste tel quel
    const rawElements = elements_additionnels?.elements || [];
    let processedElements;
    if (skipUpload) {
        processedElements = rawElements;
    } else {
        processedElements = await uploadElementsImagesToR2(rawElements);
        const hasFailedUpload = processedElements.some(el => el.type === 'image' && el.config?.imageData);
        if (hasFailedUpload) throw new Error("Échec de l'upload d'une image");
    }

    // 2. Choix QCM — upload images base64 dans les choix (éléments riches)
    let processedContenu = contenu;
    if (!skipUpload && type === 'qcm' && contenu?.choix?.length > 0) {
        const processedChoix = await Promise.all(
            contenu.choix.map(async (choix) => {
                if (choix?.type === 'image' && choix.config?.imageData) {
                    const imageUrl = await uploadImageToR2(choix.config.imageData, choix.config.fileName || 'image.png');
                    return { ...choix, config: { ...choix.config, imageUrl, imageData: null } };
                }
                return choix;
            })
        );
        processedContenu = { ...contenu, choix: processedChoix };
    }

    // 3. Assemblage final
    const finalTexte = source
        ? `<p style="font-size:10px;color:#94a3b8;margin-bottom:8px;">Tiré de ${source}</p>${texte}`
        : texte;

    return {
        texte: finalTexte,
        type,
        contenu: processedContenu,
        elements_additionnels: processedElements.length > 0
            ? { version: 1, elements: processedElements.map(el => ({ type: el.type, config: el.config })) }
            : null,
        niveau: niveau || '6eme',
        matiere: (matiere || 'maths').toLowerCase(),
        attendu: attendu || null,
        competence: competence || null,
    };
};

// ─── Parser HTML EvalNat (import de questions externes) ───

const LETTRES_IMPORT = 'ABCDEFGH';

/**
 * Parse un fichier HTML d'évaluation nationale et extrait les questions.
 * Structure attendue : <questions><question><queN>...</queN><repN>...</repN><tpeN>...</tpeN></question>...</questions>
 *
 * @param {string} htmlString - Le contenu HTML brut du fichier
 * @returns {{ source: string, questions: Array }} - La source et les questions parsées
 */

// ─── Label lisible du type de question — réutilisable partout (QuestionCard, LaunchModal, ResultatsExercice, etc.) ───
const ASSOCIATION_SUBTYPES_LABELS = {
    relier: 'Relier', ordonner: 'Ordonner', texte_trou: 'Texte à trous',
    classer: 'Classer', tableau: 'Tableau',
    // Les formats à figure (2026-10-08) : sans eux, l'écran montrait le mot technique (« placer_points_2d »).
    placer_points: 'Droite graduée', placer_points_2d: 'Repère',
    programme_construction: 'Construction', placer_aiguilles: 'Horloge',
};

/**
 * Retourne le label lisible du type d'une question.
 * Fonctionne avec Question (EvalNat) et BanqueQuestion.
 *
 * @param {Object} question - L'objet question (avec type/type_question, contenu, elements_additionnels, bonne_reponse)
 * @returns {string} - Le label ('QCM', 'QCM Multi', 'Tableau', 'Saisie', 'Relier', etc.)
 */
export const getQuestionTypeLabel = (question) => {
    if (!question) return 'QCM';
    const type = question.type || question.type_question || 'qcm';
    const contenu = question.contenu;

    // QCM Multiple
    if (type === 'qcm_multiple' || contenu?.multi) return 'QCM Multi';

    // Tableau — via elements_additionnels ou bonne_reponse [ligne|colonne]
    if (question.elements_additionnels?.elements?.some(el => el.type === 'table')) return 'Tableau';
    const br = question.bonne_reponse || contenu?.bonne_reponse || '';
    if (type === 'qcm' && typeof br === 'string' && br.includes('[') && br.includes('|')) return 'Tableau';

    // Association sous-type
    if (type === 'association' && contenu?.subType) {
        return ASSOCIATION_SUBTYPES_LABELS[contenu.subType] || contenu.subType;
    }

    // Types simples
    if (type === 'texte') return 'Saisie';
    return 'QCM';
};

/**
 * Question jouable avec les cartes ArUco (mode "En classe (cartes)") :
 * type 'qcm', pas multi-réponses, et au moins un choix (exclut les exercices-
 * tableaux : type 'qcm' à choix vide, injouables avec des cartes A-D).
 * Source de vérité unique, partagée entre QCMDashboard (compteur de la barre)
 * et LaunchModal (partition incluses/exclues au switch de format).
 */
export const estQcmSimple = (q) => q?.type === 'qcm' && !q?.contenu?.multi && (q?.contenu?.choix?.length > 0);

export const parseHTMLQuestions = (htmlString) => {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');

    // Métadonnées
    const source = doc.querySelector('name')?.textContent?.trim() || 'Import';
    const level = doc.querySelector('level')?.textContent?.trim() || '';

    // Extraire les questions
    const questionNodes = doc.querySelectorAll('question');
    const questions = [];

    questionNodes.forEach((node, i) => {
        const num = i + 1;
        const queEl = node.querySelector(`que${num}`);
        const repEl = node.querySelector(`rep${num}`);
        const tpeEl = node.querySelector(`tpe${num}`);
        const sfxEl = node.querySelector(`sfx${num}`);

        if (!queEl) return; // pas d'énoncé → skip

        const rawHTML = queEl.innerHTML || '';
        const reponse = repEl?.textContent?.trim() || '';
        const typeRaw = tpeEl?.textContent?.trim() || 'qcm1';
        const sfxRaw = sfxEl?.textContent?.trim() || '';

        // Déterminer le type TanoEd
        const isQCM = typeRaw.startsWith('qcm');
        const isCustomLine = typeRaw === 'custom-line';
        const type = isQCM ? 'qcm' : isCustomLine ? 'association' : 'texte';

        // Extraire les images base64 → elements_additionnels
        // Note : KaTeX est extrait APRÈS le split des choix (pas ici)
        const elements = [];
        let texte = rawHTML.replace(/<img[^>]+src="(data:image\/[^"]+)"[^>]*>/gi, (_, src) => {
            elements.push({ type: 'image', config: { imageData: src } });
            return `{{element:${elements.length - 1}}}`;
        });

        // Helper : convertir les $$...$$ en éléments expression
        const convertKatex = (str) => str.replace(/\$\$([^$]+)\$\$/g, (_, latex) => {
            elements.push({ type: 'expression', config: { contenu: latex.trim() } });
            return `{{element:${elements.length - 1}}}`;
        });

        // Construire le contenu selon le type
        let contenu;
        if (isQCM) {
            // Les choix QCM sont séparés par && dans l'énoncé
            const texteNormalized = texte.replace(/&amp;&amp;/g, '&&');
            const parts = texteNormalized.split(/&&/);
            if (parts.length > 1) {
                // Les choix : si c'est du KaTeX → objet {type, config}, sinon string
                const choix = parts.slice(1).map(c => {
                    let t = c.trim();
                    if (!t) return null;
                    // Nettoyer <br>, &nbsp; et guillemets parasites en début/fin de choix
                    t = t.replace(/^(<br\s*\/?>|\s|&nbsp;|")+|(<br\s*\/?>|\s|&nbsp;|")+$/gi, '').trim();
                    if (!t) return null;
                    // Ignorer les choix vides (que des tags HTML sans contenu)
                    const cleanText = t.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').replace(/\u00a0/g, '').trim();
                    if (!cleanText) return null;
                    // Réparer les $$ implicitement fermés par &&
                    if (t.startsWith('$$') && !t.endsWith('$$')) t += '$$';
                    // Si c'est entièrement du KaTeX → objet expression pour ChoixRenderer
                    const katexMatch = t.match(/^\$\$(.+)\$\$$/);
                    if (katexMatch) return { type: 'expression', config: { contenu: katexMatch[1].trim() } };
                    // Si c'est du mixte (texte + KaTeX) → garder en string, convertir les $$ en éléments
                    if (t.includes('$$')) return convertKatex(t);
                    // Sinon c'est du texte simple
                    return t;
                }).filter(Boolean);
                // Détecter QCM multi : "1;2;3;5;7" → plusieurs bonnes réponses
                const isMulti = reponse.includes(';');
                if (isMulti) {
                    const bonnesLettres = reponse.split(';')
                        .map(r => parseInt(r.trim(), 10) - 1)
                        .filter(idx => idx >= 0 && idx < choix.length)
                        .map(idx => LETTRES_IMPORT[idx]);
                    contenu = { choix, bonne_reponse: bonnesLettres, multi: true };
                } else {
                    const bonneIndex = parseInt(reponse, 10) - 1;
                    contenu = {
                        choix,
                        bonne_reponse: bonneIndex >= 0 && bonneIndex < choix.length ? LETTRES_IMPORT[bonneIndex] : 'A',
                    };
                }
            } else {
                // Pas de && → QCM sans choix détectés
                contenu = { choix: [], bonne_reponse: reponse };
            }
        } else if (isCustomLine) {
            // custom-line → texte à trous (association/texte_trou, mode saisie)
            // sfxRaw : {"inps":[["label_avant_champ","type"],...],"sfx":"suffix_final"}
            // reponse : [["valeur","type"],...] = bonnes réponses par champ
            // Pattern : inps[0][0] + {{rep[0]}} + inps[1][0] + {{rep[1]}} + ... + sfx
            // Ex Q12: inps=[["","int"],["h","int"]], rep=[["12","int"],["30","int"]] → {{12}} h {{30}}
            // Ex Q7:  inps=[["Note :","int"]], rep=[["17","int"]], sfx="/20" → Note : {{17}}/20
            let phrase = reponse;
            try {
                const repParsed = JSON.parse(reponse);
                const sfxParsed = JSON.parse(sfxRaw);
                const inps = sfxParsed?.inps || [];
                const sfx = sfxParsed?.sfx || '';

                if (Array.isArray(repParsed)) {
                    phrase = inps.map((inp, i) => {
                        const label = inp[0] || '';
                        // Si la valeur contient ";" et que les parties normalisées sont identiques
                        // → c'est juste des variantes (ex: "D;d") → on prend la première
                        let rawVal = repParsed[i]?.[0] || '';
                        if (rawVal.includes(';')) {
                            const varParts = rawVal.split(';');
                            const normalized = varParts.map(v => v.trim().toLowerCase());
                            if (new Set(normalized).size === 1) rawVal = varParts[0].trim();
                        }
                        const val = '{{' + rawVal + '}}';
                        if (!label) return val;
                        // Labels intermédiaires courts (ex: "h", "min") → espacer des deux côtés
                        // Labels longs / premiers (ex: "Note :") → juste s'assurer d'un espace avant le trou
                        const prefix = i > 0 && label.length <= 3 ? ' ' + label + ' ' : label.endsWith(' ') ? label : label + ' ';
                        return prefix + val;
                    }).join('') + sfx;
                    phrase = phrase.trim();
                }
            } catch { /* garder la réponse brute comme phrase */ }

            contenu = { subType: 'texte_trou', phrase, mode: 'saisie', distracteurs: [], strict: false, bonne_reponse: phrase };
        } else {
            // Texte libre / saisie
            const multiReponses = reponse.includes(';');
            contenu = {
                bonne_reponse: multiReponses ? reponse.split(';')[0].trim() : reponse,
                strict: !multiReponses,
                suffixe: sfxRaw || '',
            };
        }

        // Nettoyer l'énoncé : retirer les choix QCM (tout après le premier &&)
        // Puis convertir le KaTeX dans l'énoncé
        const texteForSplit = isQCM ? texte.replace(/&amp;&amp;/g, '&&') : texte;
        let enonceClean = isQCM ? texteForSplit.split(/&&/)[0].trim() : texte;
        enonceClean = enonceClean.replace(/(<br\s*\/?>|\s)+$/gi, '').trim();
        enonceClean = convertKatex(enonceClean);

        questions.push({
            index: num,
            type,
            texte: enonceClean,
            contenu,
            elements_additionnels: elements.length > 0 ? { version: 1, elements } : null,
            reponseRaw: reponse,
            typeRaw,
            selected: true, // sélectionné par défaut
            attendu: null,  // à associer par le prof
        });
    });

    return { source, level, questions };
};
